/**
 * Creator-facing copy for the reasons a project cannot be opened.
 *
 * `projectLoadError` is a plain string that `IDEShellLayout` renders VERBATIM
 * under a translated heading, so whatever the store writes is what every
 * creator reads, in every locale. The messages this replaces were hardcoded
 * English that described our storage layer rather than their situation:
 *
 *   "This imported Coogo application snapshot is missing in this browser
 *    session. Re-import the app or open it from the original import flow."
 *
 * That sentence never says what was lost (the imported app), why it is gone
 * (it was only ever saved in the browser that imported it), or what actually
 * happened (a different browser, a private window, or cleared site data). It
 * also blames the reader — "re-import" — for a limitation that was ours.
 *
 * The copy below states the fact, the cause, and the one action that works,
 * in that order, and without adjectives. Same shape as the established
 * `openpencil-import-failure-copy.ts` pattern: a closed reason set keyed by
 * locale, so a new reason cannot ship untranslated by accident.
 */

import { getLocale } from "@/pages/cogo-marketing/i18n/locale-store";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export type ProjectLoadErrorReason =
  /** The `import-…` bundle is not in THIS browser's storage. */
  | "imported-app-not-in-this-browser"
  /** The stored bundle's id disagrees with the URL's. */
  | "imported-app-url-mismatch"
  /** A `local-…` draft that was never sent anywhere. */
  | "local-draft-not-in-this-browser";

const PROJECT_LOAD_ERROR_COPY: Record<
  LocaleCode,
  Record<ProjectLoadErrorReason, string>
> = {
  en: {
    "imported-app-not-in-this-browser":
      "This imported app was saved in the browser it was imported from, and this browser does not have a copy — that is why it is empty here rather than because anything was deleted. Open it in the browser you imported it from, or import the file again here.",
    "imported-app-url-mismatch":
      "The imported app stored in this browser belongs to a different project than the one in this address. Nothing was lost; this link just points somewhere else. Open the project from Projects, or import the file again.",
    "local-draft-not-in-this-browser":
      "This draft was only ever saved in the browser that created it, so this browser has nothing to open. Go back to Projects and create a new project.",
  },
  ko: {
    "imported-app-not-in-this-browser":
      "이 임포트한 앱은 임포트한 브라우저에만 저장되어 있고, 이 브라우저에는 사본이 없습니다. 삭제된 것이 아니라 처음부터 이 브라우저에 없었습니다. 임포트했던 브라우저에서 열거나, 여기에서 파일을 다시 임포트하세요.",
    "imported-app-url-mismatch":
      "이 브라우저에 저장된 임포트 앱은 이 주소가 가리키는 프로젝트와 다릅니다. 잃어버린 것은 없고, 이 링크가 다른 곳을 가리킬 뿐입니다. 프로젝트 목록에서 열거나 파일을 다시 임포트하세요.",
    "local-draft-not-in-this-browser":
      "이 초안은 만든 브라우저에만 저장되어 있어서, 이 브라우저에는 열 것이 없습니다. 프로젝트 목록으로 돌아가 새 프로젝트를 만드세요.",
  },
  ja: {
    "imported-app-not-in-this-browser":
      "このインポート済みアプリはインポート元のブラウザにのみ保存されており、このブラウザには複製がありません。削除されたのではなく、最初からここには存在しません。インポート元のブラウザで開くか、ここでファイルを再度インポートしてください。",
    "imported-app-url-mismatch":
      "このブラウザに保存されているインポート済みアプリは、このアドレスが指すプロジェクトとは別のものです。失われたものはなく、このリンクが別の場所を指しているだけです。プロジェクト一覧から開くか、ファイルを再度インポートしてください。",
    "local-draft-not-in-this-browser":
      "この下書きは作成したブラウザにのみ保存されているため、このブラウザには開くものがありません。プロジェクト一覧に戻って新しいプロジェクトを作成してください。",
  },
  zh: {
    "imported-app-not-in-this-browser":
      "该导入的应用只保存在导入它的浏览器中，当前浏览器没有副本。它并未被删除，而是从未存在于此。请在导入它的浏览器中打开，或在此重新导入该文件。",
    "imported-app-url-mismatch":
      "此浏览器中保存的导入应用与该地址指向的项目不是同一个。没有任何内容丢失，只是这个链接指向了别处。请从项目列表中打开，或重新导入该文件。",
    "local-draft-not-in-this-browser":
      "该草稿只保存在创建它的浏览器中，当前浏览器没有可打开的内容。请返回项目列表并新建一个项目。",
  },
  "zh-TW": {
    "imported-app-not-in-this-browser":
      "這個匯入的應用程式只儲存在匯入它的瀏覽器中，目前的瀏覽器沒有副本。它並未被刪除，而是從未存在於此。請在匯入它的瀏覽器中開啟，或在此重新匯入該檔案。",
    "imported-app-url-mismatch":
      "此瀏覽器中儲存的匯入應用程式與這個網址指向的專案並非同一個。沒有任何內容遺失，只是這個連結指向了別處。請從專案清單開啟，或重新匯入該檔案。",
    "local-draft-not-in-this-browser":
      "這份草稿只儲存在建立它的瀏覽器中，目前的瀏覽器沒有可開啟的內容。請回到專案清單並建立新專案。",
  },
};

/**
 * Resolve creator copy for `reason` in the active locale.
 *
 * Read through `getLocale()` rather than a React hook because the only writers
 * are zustand actions, which run outside the component tree. `getLocale()` is
 * the same module-level source `useStudioLocale` subscribes to, so the string
 * matches the language the rest of the shell is already showing.
 */
export function projectLoadErrorMessage(reason: ProjectLoadErrorReason): string {
  const locale = getLocale();
  return (
    PROJECT_LOAD_ERROR_COPY[locale]?.[reason] ??
    PROJECT_LOAD_ERROR_COPY.en[reason]
  );
}
